import { create } from 'zustand';
import { type RootState } from '../core/state-shape';
import { http } from '../lib/http';
import { toast } from 'sonner';

type PatchOp = { op: 'add' | 'replace' | 'remove'; path: string; value?: any };

type SyncStatus = 'disconnected' | 'connecting' | 'connected' | 'stale';

type SyncState = {
  serverRev: number;
  status: SyncStatus;
  pendingState: { rev: number; state: any } | null;
  pendingPatches: { newRev: number; patches: PatchOp[] } | null;
};

type SyncActions = {
  init: (rev: number) => void;
  applyServerPatches: (newRev: number, patches: PatchOp[]) => void;
  applyServerState: (newRev: number, state: any) => void;
  applyPendingUpdates: () => void;
  reSync: () => void;
  fetchSnapshot: () => Promise<void>;
};

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPromise: Promise<void> | null = null;
let resolveCallbacks: Array<() => void> = [];
let rejectCallbacks: Array<(error: any) => void> = [];

const DEBOUNCE_DELAY = 30; // milliseconds

const useSyncStore = create<SyncState & SyncActions>((set, get) => {
  const _applyState = (state: RootState) => {
    window.dispatchEvent(new CustomEvent('apply-state', { detail: state }));
  };

  const _applyPatches = (patches: PatchOp[]) => {
    window.dispatchEvent(new CustomEvent('apply-patches', { detail: patches }));
  };

  return {
  serverRev: 0,
  status: 'disconnected',
  pendingState: null,
  pendingPatches: null,

  init: (rev) => {
    set({ serverRev: rev, status: 'connected' });
  },

  applyServerState: (newRev, state) => {
    if (window.location.pathname.startsWith('/pages/workspace/')) {
      console.log("Workspace is active, deferring full state update.");
      set({ pendingState: { rev: newRev, state }, pendingPatches: null, status: 'stale' });
      return;
    }
    set({ serverRev: newRev, status: 'connected', pendingState: null, pendingPatches: null });
    _applyState(state);
  },

  applyServerPatches: (newRev, patches) => {
    if (window.location.pathname.startsWith('/pages/workspace/')) {
      console.log(`Workspace is active, deferring ${patches.length} patches.`);
      if (get().pendingState) {
        console.log("A full state update is pending, ignoring incoming patches.");
        return;
      }
      const currentPending = get().pendingPatches;
      const newPatches = currentPending ? [...currentPending.patches, ...patches] : patches;
      set({ pendingPatches: { newRev, patches: newPatches }, status: 'stale' });
      return;
    }

    console.log(`Applying ${patches.length} patches. Rev ${get().serverRev} -> ${newRev}`);
    set({ serverRev: newRev, status: 'connected' });
    _applyPatches(patches);
  },

  applyPendingUpdates: () => {
    const { pendingState, pendingPatches } = get();
    if (!pendingState && !pendingPatches) {
      return;
    }

    if (pendingState) {
      console.log("Applying deferred full state update.");
      set({ serverRev: pendingState.rev, status: 'connected' });
      _applyState(pendingState.state);
    } else if (pendingPatches) {
      console.log(`Applying ${pendingPatches.patches.length} deferred patches.`);
      set({ serverRev: pendingPatches.newRev, status: 'connected' });
      _applyPatches(pendingPatches.patches);
    }

    set({ pendingState: null, pendingPatches: null });
  },

  reSync: () => {
    // A simple re-sync is to reload the page, which triggers a new snapshot fetch.
    window.location.reload();
  },

  fetchSnapshot: () => {
    if (pendingPromise) {
      return pendingPromise;
    }

    pendingPromise = new Promise<void>((resolve, reject) => {
      resolveCallbacks.push(resolve);
      rejectCallbacks.push(reject);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(async () => {
        try {
          const url = '/api/shell';
          const state = await http<Partial<RootState>>(url, { sendProjectId: false });

          if (!state) {
            throw new Error("Received empty shell from server");
          }

          const rev = state.metadata?.rev ?? 1;
          get().init(rev);
          get().applyServerState(rev, state);

          const callbacks = [...resolveCallbacks];
          resolveCallbacks = [];
          rejectCallbacks = [];
          callbacks.forEach(res => res());

        } catch (error) {
          console.error("Failed to fetch and apply shell:", error);
          toast.error("Failed to sync with server.", {
              description: "Could not load the application shell."
          });
          
          const callbacks = [...rejectCallbacks];
          rejectCallbacks = [];
          resolveCallbacks = [];
          callbacks.forEach(rej => rej(error));
        } finally {
          pendingPromise = null;
          debounceTimer = null;
        }
      }, DEBOUNCE_DELAY);
    });
    return pendingPromise;
  }
}});

export default useSyncStore;